"use client";

import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Script from "next/script";

import { gtmNoScript, gtmScript } from "@stadium/services/google-tag-manager";
import useGA4Analytics from "@stadium/services/google-analytics";
import { OsanoConsentChangeHandler } from "@stadium/services/osano";
import { getStoreAnalytics } from "@store/selectors/store";
import { getAnalyticsId } from "@helpers/analytics";
import { useAnalyticsContext } from "@components/provider/analytics";
import { decodeMeta } from "@helpers/route";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { BYSTADIUM_URL, SWAGMAGIC_URL } from "@stadium/helpers/app-routes";

const GlobalScripts = React.memo(({ meta = "" }) => {
  const analytics = useSelector(getStoreAnalytics);
  const { isLoading, override } = useAnalyticsContext();
  const { hostname } = decodeMeta(meta);
  const { asPath } = useRouter();

  const gtmIds = useMemo(
    () =>
      getAnalyticsId({
        asPath,
        hostname,
        isStoreLoading: isLoading,
        service: "gtm",
        analytics,
        override,
      }),
    [asPath, hostname, isLoading, analytics, override],
  );
  /* Google Tag Manager - Deferred to browser idle / first interaction */
  useEffect(() => {
    const loadGTM = () => {
      gtmIds.forEach((gtmId) => gtmScript(gtmId));
    };

    // Defer GTM until the browser is idle or the user interacts,
    // whichever comes first. This prevents 30+ tracker scripts injected
    // by GTM from blocking the main thread during initial render.
    // Absolute max wait of 3.5s ensures trackers still fire for short sessions.
    const interactionEvents = ["scroll", "click", "keydown", "touchstart"];
    let loaded = false;

    const triggerLoad = () => {
      if (loaded) return;
      loaded = true;
      interactionEvents.forEach((e) => window.removeEventListener(e, triggerLoad));
      loadGTM();
    };

    interactionEvents.forEach((e) =>
      window.addEventListener(e, triggerLoad, { once: true, passive: true }),
    );

    const fallbackTimer =
      typeof requestIdleCallback !== "undefined"
        ? requestIdleCallback(triggerLoad, { timeout: 3500 })
        : setTimeout(triggerLoad, 3500);

    return () => {
      interactionEvents.forEach((e) => window.removeEventListener(e, triggerLoad));
      if (typeof cancelIdleCallback !== "undefined" && typeof fallbackTimer === "number") {
        cancelIdleCallback(fallbackTimer);
      } else {
        clearTimeout(fallbackTimer);
      }
    };
  }, [gtmIds]);
  /* End Google Tag Manager */

  // Google Analytics GA4 - Already optimized with lazy loading
  useGA4Analytics(
    getAnalyticsId({ asPath, hostname, isStoreLoading: isLoading, service: "ga4", analytics })[0],
  );
  // End Google Analytics GA4

  const isSwagmagicDomain = hostname.includes("swagmagic");

  return (
    <>
      {process.env.NEXT_PUBLIC_ENVIRONMENT === "production" && <OsanoConsentChangeHandler />}
      {/* Schema Tag - Use lazyOnload since it's not critical for initial render */}
      <Script type="application/ld+json" id="schema-tag" strategy="lazyOnload">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "${isSwagmagicDomain ? "Swagmagic" : "Stadium"}",
            "url": "${isSwagmagicDomain ? SWAGMAGIC_URL : BYSTADIUM_URL}",
            "logo": "${
              isSwagmagicDomain
                ? "https://snackmagic.cdn.prismic.io/snackmagic/c8f62aeb-08ca-47d8-b0d7-4b1b2a264c47_swagmagic.svg"
                : "https://images.prismic.io/snackmagic/e1062c14-6497-473f-bfde-5d48b5fed323_STADIUM_Logo_Color_WhiteBG+(1).png"
            }"
          }
        `}
      </Script>
      {/* End Schema Tag */}
      {/* Prismic Toolbar */}
      <Script
        id="prismic-script"
        src="https://static.cdn.prismic.io/prismic.js?repo=snackmagic&new=true"
        strategy="lazyOnload"
      />
      {/* End Prismic Toolbar */}
      <noscript data-nosnippet>{gtmIds.map((gtmId) => gtmNoScript(gtmId))}</noscript>
    </>
  );
});

export default GlobalScripts;
